import { useEffect, useState } from 'react';

import { Grid, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import MuiDialog from 'components/MuiDialog';
import {
  SurveyDialogProps,
  TTransfusionDefaultValues,
} from 'routes/Main/Survey/type';
import useSurvey from 'store/survey/useSurvey';
import useNotification from 'hooks/useNotification';
import TextareaSection from '../../components/TextAreaSection';

import { IBloodRecord } from 'apis/survey/type';
import { updateTransfusion } from 'apis/survey';

import CommonPatientInfo from '../../components/CommonPatientInfo';
import BloodInfo from './BloodInfo';
import BloodRecords from './BloodRecords';
import Barcored from './Barcode';
import usePatient from 'store/patient/usePatient';
import { getPatientBarcode } from 'apis/admin';

const Transfusion = (props: SurveyDialogProps<TTransfusionDefaultValues>) => {
  const {
    title,
    isOpen,
    disabled,
    defaultValues,
    user_id,
    nurseName,
    patientInfo,
    onClose,
  } = props;

  const { patient, onSelectedPatientInfo } = usePatient();

  const { onUpdateIsSave } = useSurvey();
  const { onSuccess, onFail, onResultCode, onRequired } = useNotification();

  const { handleSubmit, register, getValues, setValue, watch } = useForm({
    defaultValues,
  });

  // 바코드 변수에 담기
  const [imgBarcode, setImgBarcode] = useState('');

  useEffect(() => {
    if (!patient) return;

    // 가상환자 상세정보 요청
    getPatientBarcode({ patient_id: patient.patient_id })
      .then(({ data }) => {
        setImgBarcode(data);
      })
      .catch(e => {
        onSelectedPatientInfo(null);
        onFail(`가상환자 데이터 조회에 실패했습니다.`, e);
      });
    // eslint-disable-next-line
  }, [patient]);

  const onSubmit = (data: TTransfusionDefaultValues) => {
    const {
      blood_number,
      blood_name,
      volume,
      arrival_time,
      blood_transfusion_arrival,
      transfusion_check1,
      transfusion_check2,
      transfusion_start_time,
      practitioner_start,
      transfusion_end_time,
      practitioner_end,
      transfusion_record,
      nursing_implementation,
      irradiation,
      filtering,
    } = data;

    const irradiationString = irradiation
      ? JSON.stringify({
          has_irradiation: irradiation.has_irradiation || false,
          content: irradiation.content || '',
        })
      : JSON.stringify({ has_irradiation: false, content: '' });

    const filteringString = filtering
      ? JSON.stringify({
          is_compatible: filtering.is_compatible || false,
          content: filtering.content || '',
        })
      : JSON.stringify({ is_compatible: false, content: '' });

    const request = {
      user_id,
      patient_id: patientInfo.patient_id,
      transfusion_survey: {
        transfusion_information: {
          blood_number,
          blood_name,
          volume,
          arrival_time,
          blood_transfusion_arrival,
          transfusion_check1,
          transfusion_check2,
          transfusion_start_time,
          practitioner_start,
          transfusion_end_time,
          practitioner_end,
          nursing_implementation,
          irradiation: irradiationString, // JSON 문자열로 변환
          filtering: filteringString, // JSON 문자열로 변환
        },
        transfusion_record: transfusion_record?.map(
          ({
            time,
            division,
            sbp,
            dbp,
            pr,
            rr,
            bt,
            side_effects,
            notes,
          }: IBloodRecord) => ({
            time,
            division,
            sbp,
            dbp,
            pr,
            rr,
            bt,
            side_effects,
            notes,
          })
        ),
      },
    };

    updateTransfusion(request)
      .then(({ data: { rc } }) => {
        if (rc !== 1) return onResultCode(rc);

        onUpdateIsSave(true);
        onSuccess('저장되었습니다');
      })
      .catch(e => onFail('저장에 실패하였습니다', e));
  };
  const formProps = {
    disabled,
    watch,
    register,
    getValues,
    setValue,
    onSuccess,
    onRequired,
  };

  const [bloodBarcod, setBloodBarcode] = useState(
    getValues('blood_number') ? getValues('blood_number') : ''
  );

  const [BloodBarcodeImg, setBloodBarcodeImg] = useState('');

  return (
    <MuiDialog.SurveyForm
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={disabled ? undefined : handleSubmit(onSubmit)}
      update_at={defaultValues?.update_at}
    >
      <Grid
        container
        wrap="wrap"
        rowSpacing={5}
        columnSpacing={3}
        sx={{ py: 5, px: 1 }}
      >
        <Typography
          sx={{
            margin: '40px auto 0px auto',
            fontWeight: '700',
            fontSize: '16px',
            textAlign: 'center',
          }}
        >
          수혈 기록지
        </Typography>
        <Barcored imgBarcode={imgBarcode} BloodBarcodeImg={BloodBarcodeImg} />
        <CommonPatientInfo patientInfo={patientInfo} nurseName={nurseName} />
        <BloodInfo
          {...formProps}
          blood={patientInfo.blood}
          bloodBarcod={bloodBarcod}
          setBloodBarcode={setBloodBarcode}
          patient_id={patientInfo.patient_id}
          user_id={user_id}
          setBloodBarcodeImg={setBloodBarcodeImg}
        />
        <BloodRecords {...formProps} />
        <TextareaSection
          {...formProps}
          title="간호 수행"
          registerId="nursing_implementation"
          required={false}
        />
      </Grid>
    </MuiDialog.SurveyForm>
  );
};

export default Transfusion;
